// École Nid Douillet - Authentication Store
// Zustand store for authentication state management

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, LoginCredentials, ChangePasswordRequest } from '@/types/index';
import { authApi, TokenManager } from '@services/api';
import toast from 'react-hot-toast';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  getCurrentUser: () => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<boolean>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  
  // Utilities
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  isDirector: () => boolean;
  isParent: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login action
      login: async (credentials: LoginCredentials): Promise<boolean> => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.login(credentials);
          
          if (response.success && response.data) {
            const { user } = response.data;
            
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            
            toast.success(`Bienvenue, ${user.firstName} !`);
            return true;
          } else {
            const errorMessage = response.message || 'Erreur de connexion';
            set({
              isLoading: false,
              error: errorMessage,
            });
            
            toast.error(errorMessage);
            return false;
          }
        } catch (error) {
          const errorMessage = 'Erreur de connexion. Veuillez réessayer.';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          toast.error(errorMessage);
          return false;
        }
      },

      // Logout action
      logout: async (): Promise<void> => {
        set({ isLoading: true });
        
        try {
          await authApi.logout();
        } catch (error) {
          console.warn('Logout API call failed:', error);
        } finally {
          // Always clear local state regardless of API response
          TokenManager.clearTokens();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          
          toast.success('Déconnexion réussie');
        }
      },

      // Refresh token action
      refreshToken: async (): Promise<boolean> => {
        try {
          const response = await authApi.refreshToken();
          
          if (response.success) {
            // Token refreshed successfully, get updated user info
            await get().getCurrentUser();
            return true;
          } else {
            // Refresh failed, logout user
            await get().logout();
            return false;
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          await get().logout();
          return false;
        }
      },

      // Get current user action
      getCurrentUser: async (): Promise<void> => {
        const token = TokenManager.getAccessToken();
        if (!token) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }

        set({ isLoading: true });
        
        try {
          const response = await authApi.getCurrentUser();
          
          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            // Invalid token or user not found
            TokenManager.clearTokens();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          }
        } catch (error) {
          console.error('Get current user failed:', error);
          TokenManager.clearTokens();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      // Change password action
      changePassword: async (data: ChangePasswordRequest): Promise<boolean> => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.changePassword(data);
          
          if (response.success) {
            set({ isLoading: false });
            toast.success('Mot de passe modifié avec succès');
            return true;
          } else {
            const errorMessage = response.message || 'Erreur lors de la modification du mot de passe';
            set({
              isLoading: false,
              error: errorMessage,
            });
            
            toast.error(errorMessage);
            return false;
          }
        } catch (error) {
          const errorMessage = 'Erreur lors de la modification du mot de passe';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          toast.error(errorMessage);
          return false;
        }
      },

      // Clear error action
      clearError: () => {
        set({ error: null });
      },

      // Set loading action
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // Utility functions
      hasRole: (role: string): boolean => {
        const { user } = get();
        return user?.role === role;
      },

      hasPermission: (permission: string): boolean => {
        const { user } = get();
        return user?.permissions?.includes(permission) || false;
      },

      isDirector: (): boolean => {
        return get().hasRole('DIRECTOR');
      },

      isParent: (): boolean => {
        return get().hasRole('PARENT');
      },
    }),
    {
      name: 'ecole-auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Validate token on app startup
        if (state?.isAuthenticated && TokenManager.getAccessToken()) {
          state.getCurrentUser();
        } else {
          // Clear invalid state
          state?.logout();
        }
      },
    }
  )
);

// Selectors for common use cases
export const useAuth = () => {
  const store = useAuthStore();
  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    login: store.login,
    logout: store.logout,
    changePassword: store.changePassword,
    clearError: store.clearError,
    hasRole: store.hasRole,
    hasPermission: store.hasPermission,
    isDirector: store.isDirector,
    isParent: store.isParent,
  };
};

export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
